import { api, ensureApiAuthenticated } from '../services/api';

export interface DashboardEnvironmentContext {
  environment: string;
  build_sha: string | null;
  api_version: string;
}

export interface DashboardChannelBacklog {
  channel: string;
  pending_count: number;
  processing_count: number;
  failed_attempt_count: number;
  retry_alert_count: number;
  oldest_pending_age_seconds: number;
  oldest_processing_age_seconds?: number;
  stuck_processing_count?: number;
}

export interface DashboardChannelHealth {
  channel: string;
  state: string;
  completed_attempts: number;
  failed_attempts: number;
  last_success_at: string | null;
  last_attempt_at: string | null;
}

export interface DashboardMetricAlertState {
  metric_name: string;
  threshold_type: string;
  threshold_value: number;
  severity: string;
  current_value: number;
  state: string;
}

export interface ChannelOperationsDashboard {
  generated_at: string;
  dashboard_status: 'healthy' | 'degraded';
  degraded_sections: string[];
  environment: DashboardEnvironmentContext | { status: 'degraded'; error: string };
  fanout: {
    generated_at?: string;
    metrics: Record<string, Record<string, number>>;
    alert_thresholds: Record<string, Record<string, string | number>>;
    alert_states: DashboardMetricAlertState[];
    status?: 'degraded';
    error?: string;
  };
  outbox: {
    channel_backlog: DashboardChannelBacklog[];
    oldest_pending_age_seconds: number;
    oldest_processing_age_seconds?: number;
    stuck_processing_count?: number;
    stuck_processing_alert_seconds?: number;
    stuck_processing_alert_state?: 'ok' | 'critical' | string;
    stuck_processing_jobs?: Array<{
      queue_id: number;
      status: string;
      retry_count: number;
      channel: string;
      last_error: string | null;
      created_at: string | null;
      processing_started_at?: string | null;
      updated_at?: string | null;
      processing_age_seconds: number;
    }>;
    replay_operations_last_24h: number;
    last_replay_key: string | null;
    last_replay_at: string | null;
    credential_failure_count?: number;
    credential_failures?: Array<{
      queue_id: number;
      status: string;
      retry_count: number;
      channel: string;
      last_error: string;
      created_at: string | null;
    }>;
    status?: 'degraded';
    error?: string;
  };
  channels: {
    channels: DashboardChannelHealth[];
    manapool_reconciliation: Record<string, unknown>;
    status?: 'degraded';
    error?: string;
  };
  reconciliation: {
    last_run: {
      status: string;
      synced_at: string | null;
      copies_created: number;
      copies_deleted: number;
      copies_unchanged: number;
      policy_mode: string | null;
      error_message: string | null;
    } | null;
    discrepancy_counts: Record<string, number>;
    policy_action_counts: Record<string, number>;
    pending_suggested_actions: number;
    applied_actions: number;
    shipped_shortfall_with_stock?: {
      count: number;
      alert_state: string;
      summary?: {
        line_count: number;
        total_shortfall_qty: number;
        total_in_stock_now: number;
      };
      sample?: Array<Record<string, unknown>>;
    };
    status?: 'degraded';
    error?: string;
  };
  freshness: {
    mtgjson: {
      last_run_at: string | null;
      status: string | null;
      tracked_version: string | null;
      load_status: {
        overall_status: string;
        current_step?: string | null;
      };
    };
    pricing_snapshots_last_24h: number;
    status?: 'degraded';
    error?: string;
  };
  platform_health: {
    checked_at: string;
    state: 'healthy' | 'warning' | 'critical';
    vault: { ok: boolean; state: string; message: string; checked_at?: string | null };
    webhooks: { manapool_secret_configured: boolean };
    issues: string[];
    status?: 'degraded';
    error?: string;
  };
}

export async function fetchChannelOperationsDashboard(): Promise<ChannelOperationsDashboard> {
  await ensureApiAuthenticated();
  const response = await api.get('/dashboards/channel-operations');
  return response.data;
}